import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode.react';
import Header from './Header';
import { generateLoginQRData, pollLoginResult } from '../services/api';

const NewLoginPage = ({ onLogin }) => {
  const [qrData, setQrData] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 60 seconds countdown
  const [isPolling, setIsPolling] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null);

  // Generate QR code data using API service
  const generateQRData = async () => {
    setIsRefreshing(true);
    setLoginError('');
    
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    try {
      const data = await generateLoginQRData();
      setQrData(data);
      setTimeLeft(60); // Reset timer to 60 seconds
      
      // Start polling if we got a valid session
      const sessionId = sessionStorage.getItem('login_session');
      if (sessionId) {
        startPolling(sessionId);
      }
    } catch (error) {
      console.error('Failed to generate QR data:', error);
      // Show error to user instead of using mock data
      setLoginError('Unable to generate login QR code. Please try again or use email login.');
      setQrData(''); // Clear any previous QR data
      setTimeLeft(0); // Stop timer
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Start polling for presentation result
  const startPolling = (sessionId) => {
    setIsPolling(true);
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await pollLoginResult(sessionId);
        if (result.success && result.verified) {
          clearInterval(pollingIntervalRef.current);
          setIsPolling(false);
                    
          if (onLogin) {
            onLogin({
              email: result.email,
              userId: result.userId,
              name: result.claims?.given_name || result.claims?.name || 'Wallet User',
              loginMethod: 'wallet'
            });
          }
          
          sessionStorage.removeItem('login_session');
          
          navigate('/');
        } else if (!result.pending && result.error) {
          console.error('Login verification failed:', result.error);
          setLoginError(result.error);
          clearInterval(pollingIntervalRef.current);
          setIsPolling(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 2 * 1000); // Poll every 2 seconds
  };

  // Initialize QR data
  useEffect(() => {
    generateQRData();
    
    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      sessionStorage.removeItem('login_session');
    };
  }, []);

  // Countdown timer (no automatic refresh)
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Stop at 0, don't auto-refresh
          // Also stop polling when expired
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            setIsPolling(false);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEmailLoginClick = () => {
    navigate('/login');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Header />

      {/* Main Content */}
      <main style={{ 
        padding: '60px 0', 
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.58), rgba(255, 255, 255, 0.9)),url("/images/living-room-scandinavian-interior-design.jpg")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '91vh'
      }}>
        <div className="container">
          <div className="card">
          {/* Login Title */}
          <h2 className="text-center" style={{ marginBottom: '40px', color: '#333' }}>
            Login
          </h2>

          <div>
            <p style={{
              fontSize: '16px',
              marginBottom: '20px',
              color: '#333',
              textAlign: 'center'
            }}>
              Scan the QR Code with your Wallet
            </p>

            {/* Timer or Refresh Link */}
            {timeLeft > 0 ? (
              <p style={{
                fontSize: '14px',
                marginBottom: '20px',
                color: '#666',
                textAlign: 'center'
              }}>
                This QR Code will expire in <strong>{formatTime(timeLeft)}</strong>
              </p>
            ) : (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <button
                  onClick={generateQRData}
                  disabled={isRefreshing}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    textDecoration: 'underline',
                    cursor: isRefreshing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: isRefreshing ? 0.6 : 1
                  }}
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh QR code'}
                </button>
              </div>
            )}

            {/* Status Message */}
            {isPolling && (
              <p style={{
                fontSize: '14px',
                marginBottom: '10px',
                color: '#2196F3',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                Waiting for wallet verification...
              </p>
            )}
            
            {loginError && (
              <p style={{
                fontSize: '14px',
                marginBottom: '10px',
                color: '#dc3545',
                textAlign: 'center'
              }}>
                {loginError}
              </p>
            )}

            {/* QR Code Container */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
              position: 'relative'
            }}>
              <div style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative'
              }}>
                {/* Check if we have QR data */}
                {qrData ? (
                  qrData.startsWith('data:image') ? (
                    // Display base64 image directly
                    <img 
                      src={qrData} 
                      alt="Login QR Code" 
                      style={{ width: 200, height: 200 }}
                    />
                  ) : (
                    // Generate QR code from URI
                    <QRCode 
                      value={qrData}
                      size={200}
                      level="M"
                    />
                  )
                ) : (
                  // No QR data - show placeholder
                  <div style={{
                    width: 200,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: '#6c757d' }}>
                      {loginError ? 'Error' : 'Loading...'}
                    </span>
                  </div>
                )}
                {(isRefreshing || timeLeft === 0) && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={generateQRData}>
                      ⟳
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Link */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#333',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Tap here if you are on mobile
              </button>
            </div>

            {/* Separator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#ddd'
              }}></div>
              <span style={{
                padding: '0 15px',
                color: '#666',
                fontSize: '14px'
              }}>or</span>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#ddd'
              }}></div>
            </div>

            {/* Navigate to Email Login Button */}
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={handleEmailLoginClick}
                className="btn btn-secondary"
              >
                Use Email & Password Instead
              </button>
            </div>
          </div>

          {/* Register Link */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              Don't have an account?
            </p>
            <Link to="/register" style={{ color: '#90c695' }}>
              Register Now
            </Link>
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewLoginPage;